import * as React from "react";
import { Map } from "ol";
import { FC, useEffect, useRef, useState } from "react";
import { AdvancedDropdown } from "./AdvancedDropdown";
import { IMultiRowList } from "./DropdownList";
import { convertToString } from "./CoordinateUtils";
import { useMapContext } from "../../Contexts/MapContext";
import { DropdownType } from "../enums";
import i18next from "i18next";
import "./CoordinateWidget.scss";

export interface ICoords {
    value: string;
    labelRows: [string, string];
}

interface ICoordinateWidget {
    map: Map;
}

const xArrow = "\u2192 ";
const yArrow = "\u2191 ";
const replacement = "x(N) " + yArrow + ", y(E) " + xArrow;
const replacement2 = "x(E) " + xArrow + ", y(N) " + yArrow;

export const CoordinateWidget: FC<ICoordinateWidget> = (props: ICoordinateWidget) => {
    const { configA, formatType, setFormatType } = useMapContext();
    const [currentPoint, setCurrentPoint] = useState<number[]>();
    const [allCoordinates, setAllCoordinates] = useState<ICoords[]>([]);
    const [coordinates, setCoordinates] = useState<string>(convertToString("LKS", props.map.getView().getCenter()));
    const [formatName, setFormatName] = useState("LKS92");
    const [expanded, setExpanded] = useState<boolean>(false);

    const formatTypeRef = useRef<string>(formatType);

    useEffect(() => {
        setCurrentPoint(props.map.getView().getCenter());
        props.map.on("pointermove", onPointerMove);

        return () => props.map.un("pointermove", onPointerMove);
    }, []);

    useEffect(() => {
        formatTypeRef.current = formatType;
    }, [formatType]);

    useEffect(() => {
        expanded &&
            currentPoint &&
            setAllCoordinates(
                configA.coordinateFormats.map((format: string) => {
                    const coordinates = convertToString(format, currentPoint);
                    const coordFormat =
                        format === "LKS"
                            ? format + "92 x(E) " + xArrow + ", y(N) " + yArrow
                            : format === "LKSGEO"
                            ? format.replace("GEO", "") + "92 TM x(N) " + yArrow + ", y(E) " + xArrow
                            : format;
                    return { value: format, labelRows: [coordFormat, coordinates] };
                })
            );
    }, [currentPoint, expanded]);

    const onPointerMove = (e) => {
        const newCoordinates = convertToString(formatTypeRef.current, [e.coordinate[0], e.coordinate[1]]);
        setCurrentPoint(e.coordinate);
        setCoordinates(newCoordinates);
    };

    const changeFormat = (attribute?: IMultiRowList) => {
        if (!attribute) {
            return;
        }

        const formatNameNew = attribute.labelRows[0].toString().replace(replacement, "").replace(replacement2, "");
        const formatTypeNew = attribute.value.toString();
        setFormatType(formatTypeNew);
        setFormatName(formatNameNew);
        setCoordinates(convertToString(formatTypeNew, currentPoint));
    };

    return (
        <div className="coordinate-widget">
            <AdvancedDropdown
                title={i18next.t("coordinates.typeTitle")}
                attributes={allCoordinates}
                text={formatName + " " + coordinates}
                alwaysOpen={false}
                closeOnSubmit={true}
                selectedValue={formatType}
                type={DropdownType.MultiLine}
                clicked={(at: IMultiRowList) => changeFormat(at)}
                chevronClass={["esri-icon-up", "esri-icon-down"]}
                onToggle={(isExpanded) => setExpanded(isExpanded)}
            />
        </div>
    );
};
